import { Component, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FileService } from '../../../../../shared/services/file.service';
import { FileUploadProgressComponent } from '../../../../../shared/components/file-upload-progress/file-upload-progress.component';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

interface UploadItem {
  file: File;
  documentName: string;
  category: string;
}

@Component({
  selector: 'app-upload-document-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, FileUploadProgressComponent],
  templateUrl: './upload-document-modal.component.html',
  styleUrls: ['./upload-document-modal.component.css']
})
export class UploadDocumentModalComponent implements OnDestroy {
  @Input() collectionName: string = '';
  @Input() categories: string[] = [];
  @Input() selectedCategory: string = '';
  @Output() uploadConfirmed = new EventEmitter<{ uploads: UploadItem[] }>();

  documentName: string = '';
  newCategoryName: string = '';
  isAddingNewCategory: boolean = false;
  selectedFiles: File[] = [];
  dragOver: boolean = false;
  isUploading: boolean = false;
  isReadOnly:boolean=false;
  private uploadSubscription?: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private fileService: FileService,
    private toastr: ToastrService
  ) {}

  onFileSelected(event: any): void {
    const files = event.target.files as FileList;
    if (files?.length) {
      this.addFiles(Array.from(files));
      event.target.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(Array.from(files));
    }
  }

onUpload(): void {
  const hasMultipleFiles = this.selectedFiles.length > 1;

  // Comprehensive validation
  if (!hasMultipleFiles && (!this.documentName || !this.documentName.trim())) {
    this.toastr.error('Please enter a document name.');
    return;
  }

  if (this.selectedFiles.length === 0) {
    this.toastr.error('Please select one or more files to upload.');
    return;
  }

  if (!this.selectedCategory || !this.selectedCategory.trim()) {
    this.toastr.error('Please select a document category.');
    return;
  }

  if (this.isAddingNewCategory && !this.newCategoryName.trim()) {
    this.toastr.error('Please enter a new document category.');
    return;
  }

  if (this.isReadOnly && this.selectedFiles.length > 1) {
    this.toastr.error('Only one file can be uploaded for this action.');
    return;
  }

  // Re-validate files before closing modal
  const invalidFile = this.selectedFiles.find(file => !this.fileService.validateFile(file, this.isSkillSetCollection()));
  if (invalidFile) {
    this.toastr.error(`Invalid file selected: ${invalidFile.name}`);
    this.removeSelectedFile(invalidFile);
    return;
  }

  if (this.isUploading) {
    this.toastr.warning('Upload is already in progress.');
    return;
  }

  this.isUploading = true;

  const uploads: UploadItem[] = this.selectedFiles.map((file, index) => {
    const fallbackName = this.getDocumentNameFromFile(file);
    const documentName = hasMultipleFiles
      ? fallbackName
      : (index === 0 && this.documentName.trim() ? this.documentName.trim() : fallbackName);

    return {
      file,
      documentName,
      category: this.getSelectedCategoryValue()
    };
  });

  this.uploadConfirmed.emit({ uploads });
  this.activeModal.close({ uploads });
}

  onCancelUpload(): void {
    // If upload is in progress, cancel it
    if (this.isUploading && this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = undefined;
      this.isUploading = false;
      this.toastr.info('Upload cancelled.');
    }
    
    // Reset form
    this.selectedFiles = [];
    this.documentName = '';
  }

  get firstSelectedFileSize(): string {
    if (this.selectedFiles.length > 0) {
      return this.fileService.formatFileSize(this.selectedFiles[0].size);
    }
    return '';
  }

  get totalSelectedSize(): string {
    const total = this.selectedFiles.reduce((size, file) => size + file.size, 0);
    return this.fileService.formatFileSize(total);
  }

  getFileSize(file: File): string {
    return this.fileService.formatFileSize(file.size);
  }


  onCancel(): void {
    // Prevent closing during upload
    if (this.isUploading) {
      this.toastr.warning('Please wait for the upload to complete.');
      return;
    }
    
    this.activeModal.dismiss();
  }

  onCategoryChange(value: string): void {
    if (value === '__add_new__') {
      this.isAddingNewCategory = true;
      this.selectedCategory = value;
      return;
    }
    this.isAddingNewCategory = false;
    this.selectedCategory = value;
    this.newCategoryName = '';
  }

  getDropdownCategories(): string[] {
    return Array.from(new Set((this.categories || []).filter(c => !!c && c.trim().length > 0)));
  }

  private getSelectedCategoryValue(): string {
    if (this.selectedCategory === '__add_new__') {
      return this.newCategoryName.trim();
    }
    return this.selectedCategory.trim();
  }
onDocumentNameChange(value: string): void {
  if (!value) {
    this.documentName = '';
    return;
  }

  // 1️⃣ Replace multiple spaces with a single space
  let cleaned = value.replace(/\s+/g, ' ').trim();

  // 2️⃣ Prevent leading or trailing spaces
  this.documentName = cleaned;
}

isSkillSetCollection(): boolean {
  return this.collectionName?.toLowerCase().includes('claims');
}

getAcceptedFileTypes(): string {
  if (this.isSkillSetCollection()) {
    return '.docx,.pdf,.png,.jpg,.jpeg,.bmp,.tiff,.ppt,.pptx';
  }

  return '.docx,.pdf';
}

getSupportedFormatsText(): string {
  if (this.isSkillSetCollection()) {
    return '.docx, .pdf, .png, .jpg, .jpeg, .bmp, .tiff, .ppt, .pptx';
  }

  return '.docx, .pdf';
}

removeSelectedFile(fileToRemove: File): void {
  this.selectedFiles = this.selectedFiles.filter(file =>
    !(file.name === fileToRemove.name && file.size === fileToRemove.size && file.lastModified === fileToRemove.lastModified)
  );

  if (this.selectedFiles.length === 0) {
    this.documentName = '';
  }
}

private addFiles(files: File[]): void {
  const skillSetCollection = this.isSkillSetCollection();

  for (const file of files) {
    if (!this.fileService.validateFile(file, skillSetCollection)) {
      continue;
    }

    const alreadySelected = this.selectedFiles.some(existing =>
      existing.name === file.name &&
      existing.size === file.size &&
      existing.lastModified === file.lastModified
    );

    if (!alreadySelected) {
      this.selectedFiles = [...this.selectedFiles, file];
    }
  }

  if (this.selectedFiles.length > 0 && !this.documentName) {
    this.documentName = this.getDocumentNameFromFile(this.selectedFiles[0]);
  }
}

private getDocumentNameFromFile(file: File): string {
  return file.name.replace(/\.[^/.]+$/, '');
}

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    if (this.uploadSubscription && !this.uploadSubscription.closed) {
      this.uploadSubscription.unsubscribe();
    }
    this.uploadSubscription = undefined;
  }
}
